import _ from '@lodash';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormControl, Select, FormHelperText, InputLabel, MenuItem, ListItemText, Checkbox, IconButton } from '@material-ui/core';
import { Lock, ArrowDropDown, Clear } from '@material-ui/icons';
import clsx from 'clsx';
import { determineEditableAccess } from '../EditFormUtils';

function EditFormSelectField(props) {
	const methods = useFormContext();
	const { control, getValues } = methods;
	const { t } = useTranslation(props.config.topic ? ['editform', props.config.topic] : 'editform');
	const { error } = props.fieldState;
	const { value, onChange } = props.field;

	const [items, setItems] = useState(null);
	const [readOnly, setReadOnly] = useState(determineEditableAccess(props.config, props.fieldConfig, props.fieldPrefix));

	const valueKey = determineValueKey(props.fieldConfig);
	const labelKey = determineLabelKey(props.fieldConfig);
	const hideNull = props.fieldConfig.hideNull || false;
	const { compact = false } = props;

	const isMultiple = !!props.fieldConfig.multiple;
	const selectValue = isMultiple ? (Array.isArray(value) ? value : []) : (value ?? '');
	const hasClearValue = isMultiple ? Array.isArray(value) && value.length > 0 : value !== null && value !== undefined && value !== '';

	const parseFilterKey = () => {
		if (props.fieldConfig.filterKey) {
			if (_.isArray(props.fieldConfig.filterKey)) {
				return props.fieldConfig.filterKey.map(f => (!f.startsWith('^') ? (props.fieldPrefix || '') + f : f.substring(1)));
			}
			return !props.fieldConfig.filterKey.startsWith('^') ? (props.fieldPrefix || '') + props.fieldConfig.filterKey : props.fieldConfig.filterKey.substring(1);
		}
		return null;
	};
	const filterKey = parseFilterKey();

	const filterValue = useWatch({
		control,
		name: filterKey,
		disabled: filterKey == null
		// defaultValue: null TODO: gondot okoz ha benne van
	});

	const updateItems = () => {
		if (_.isFunction(props.fieldConfig.items)) {
			props.fieldConfig.items((props.fieldConfig.itemsParameterIsDto ? getValues() : filterValue), props.field).then(result => {
				setItems(result || []);
			});
		} else if (Array.isArray(props.fieldConfig.items)) {
			setItems(props.fieldConfig.items);
		} else {
			setItems([]);
		}
	};

	useEffect(() => {
		updateItems();
	}, [props.fieldConfig, filterValue]);

	const renderValue = selected => {
		if (isMultiple) {
			const arr = Array.isArray(selected) ? selected : [];
			return (items || [])
				.filter(d => arr.indexOf(d[valueKey]) > -1)
				.map(d => d[labelKey])
				.join(', ');
		}

		if (selected !== undefined && selected !== null && items) {
			const itemIndex = items.findIndex(d => selected === d[valueKey]);
			if (itemIndex > -1) return items[itemIndex][labelKey];
		}
		return '';
	};

	useEffect(() => {
		if (_.isFunction(props.fieldConfig.onChange)) props.fieldConfig.onChange(value, props.field, methods);
	}, [value]);

	const selectLabel = () => {
		return compact ? undefined : !_.isUndefined(props.fieldConfig.text) ? props.fieldConfig.text : t(`${props.config.topic}:${props.fieldConfig.key}`);
	};

	const handleClear = event => {
		event.preventDefault();
		event.stopPropagation();
		onChange(isMultiple ? [] : null);
	};

	return (
		<FormControl className={clsx('relative', compact ? 'w-full' : 'w-full mt-8 mb-16', props.fieldConfig.className)} variant="outlined" error={error} required={props.fieldConfig.required}>
			{!compact && <InputLabel id={`${props.field.name}-label`}>{selectLabel()}</InputLabel>}

			{items != null ? (
				<>
					<Select
						labelId={compact ? null : `${props.field.name}-label`}
						id={props.field.name}
						multiple={isMultiple}
						label={selectLabel()}
						value={selectValue}
						onChange={e => onChange(e.target.value)}
						inputProps={{ readOnly }}
						renderValue={selected => renderValue(selected)}
						IconComponent={readOnly ? Lock : ArrowDropDown}
						displayEmpty={!hideNull}
					>
						{!hideNull ? <MenuItem value={null}>{props.fieldConfig.emptyLabel || t('SELECT_EMPTY')}</MenuItem> : null}

						{isMultiple
							? items.map(item => (
								<MenuItem key={item[valueKey]} value={item[valueKey]}>
									<Checkbox checked={Array.isArray(selectValue) && selectValue.indexOf(item[valueKey]) > -1} />
									<ListItemText primary={item[labelKey]} />
								</MenuItem>
							))
							: items.map(item => (
								<MenuItem key={item[valueKey]} value={item[valueKey]}>
									{item[labelKey]}
								</MenuItem>
							))}
					</Select>

					{!readOnly && !hideNull && hasClearValue ? (
						<IconButton
							className="absolute right-28 top-1/2 transform -translate-y-1/2 z-10 p-4"
							size="small"
							tabIndex={-1}
							onMouseDown={event => {
								event.preventDefault();
								event.stopPropagation();
							}}
							onClick={handleClear}
						>
							<Clear fontSize="small" />
						</IconButton>
					) : null}
				</>
			) : null}

			{error ? <FormHelperText>{error.message}</FormHelperText> : null}
		</FormControl>
	);
}

export const determineValueKey = fieldConfig => {
	return !_.isUndefined(fieldConfig.type) && fieldConfig.type === 'enum' ? 'value' : 'id';
};

export const determineLabelKey = fieldConfig => {
	return !_.isUndefined(fieldConfig.type) && fieldConfig.type === 'enum' ? 'label' : 'name';
};

export default EditFormSelectField;
